import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'

// css
import 'rc-slider/assets/index.css'
import 'react-quill/dist/quill.snow.css'

// slices
import { projectsState } from './projects/projects.slice'
import {
    isLoggedInLogin,
    isUserAuthenticated,
    loginState,
    setPendingWidgetRedirect,
} from './login/login.slice'

// pages
import FoF from './404/404.index'

// component sections
import Layout from '../layouts/sidebar/sidebar'
import TimeTrackingLandingPage from './time-tracking/timeTracking.section'

// css
import './client.module.css'

// routes
import {
    isProjectWidgetMode,
    PRIVATE_ROUTES,
    PUBLIC_ROUTES,
    ROOT_ROUTE,
} from '../routes'

// pages
import AuthorizationCallBackPage from './callbackPage'

// constants
import { CHECK_PUBLIC_ROUTE_LOGIN } from '../constants/roles'

// helpers
import { checkRoutePermission } from '../helpers/routecheckPermission'
import { buildWidgetRedirectFromLocation } from '../helpers/widgetRedirect'
import { isWidgetProjectPath } from '../helpers/widgetAuth'
import { WidgetProjectRouteElements } from './allRoutes/widgetProjectRoutes'

// PRIVATE / PUBLIC ROUTES
import PublicRoute from './allRoutes/publicRoute'
import PrivateRoute from './allRoutes/privateRoute'
import PublicRoutesList from './allRoutes/publicRoutesList'
import ApplyForJobPublicPage from './jobs/publicPage/applyForJob'

const isPublicJobApplyPath = (pathname = '') =>
    /^\/jobs\/[^/]+\/apply\/?$/.test(pathname)

const Client = () => {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const params = useLocation()

    const { userProfile, status } = useSelector(loginState)

    const { clientModalProjectsListData = [], clientModalTotalProjects = 0 } =
        useSelector(projectsState)

    const isWidgetEmbed =
        isProjectWidgetMode(params.search) &&
        isWidgetProjectPath(params.pathname)

    // CHECK IS LOGGED IN OR NOT (skipped for widget embeds — they use widget JWT)
    async function checkIsLogin() {
        if (CHECK_PUBLIC_ROUTE_LOGIN.includes(params.pathname)) {
            return
        }

        if (isPublicJobApplyPath(params.pathname)) {
            return
        }

        if (isWidgetEmbed) {
            return
        }

        const checkLogin = await dispatch(isLoggedInLogin())
        const checkSuccessLogin = checkLogin?.payload?.data
        const { success: checkSuccess } = checkSuccessLogin

        if (!checkSuccess) {
            if (params.pathname === ROOT_ROUTE) {
                navigate(PUBLIC_ROUTES.login)
                return
            }

            const publicPaths = Object.values(PUBLIC_ROUTES).filter(
                (route) => typeof route === 'string'
            )
            const isPublicPath = publicPaths.includes(params.pathname)
            if (isPublicPath) {
                return
            }

            const privatePaths = Object.values(PRIVATE_ROUTES)
                .map((route) =>
                    typeof route === 'string' ? route : route?.root
                )
                .filter((route) => typeof route === 'string')

            const isPrivatePath =
                params.pathname.startsWith('/dashboard') ||
                privatePaths.includes(params.pathname)

            if (isPrivatePath) {
                const widgetRedirect = buildWidgetRedirectFromLocation(
                    params.pathname,
                    params.search
                )
                if (widgetRedirect) {
                    dispatch(setPendingWidgetRedirect(widgetRedirect))
                }
                navigate(PUBLIC_ROUTES.login)
            }
        }
    }

    useEffect(() => {
        checkIsLogin()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, params.pathname, params.search])

    return (
        <Routes>
            <Route
                path={PUBLIC_ROUTES.timeTrackingApp}
                element={<TimeTrackingLandingPage />}
            />
            <Route
                path={PUBLIC_ROUTES.callBackUrl}
                element={<AuthorizationCallBackPage />}
            />
            <Route
                path={PUBLIC_ROUTES.jobCandidateDetail(':id')}
                element={<ApplyForJobPublicPage />}
            />
            <Route element={<PublicRoute />}>{PublicRoutesList()}</Route>
            {(isWidgetEmbed || status !== 'loading') &&
                !isUserAuthenticated(userProfile) &&
                WidgetProjectRouteElements()}
            {status !== 'loading' && isUserAuthenticated(userProfile) && (
                <Route element={<PrivateRoute />}>
                    <Route path={ROOT_ROUTE} element={<></>} />
                    <Route element={<Layout userProfile={userProfile} />}>
                        {checkRoutePermission(
                            userProfile,
                            clientModalProjectsListData,
                            clientModalTotalProjects
                        )}
                    </Route>
                    <Route path={'*'} element={<FoF />} />
                </Route>
            )}
        </Routes>
    )
}

export default Client
